// =============================================================================
// screens/CommunityManageScreen.tsx — a CRYB Leader's per-community control panel.
//
// Reached from the Leader Dashboard ("Manage"). Surfaces real per-community
// analytics (members / posts / active this week / top posts) plus quick actions
// into the creator tools that already exist (edit appearance, members, channels).
// Stage 4 of the leader flow. Read APIs are public; edit actions stay owner-gated
// on their own screens.
// =============================================================================
import { useCallback, useState, useLayoutEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import { GlassCard, GlassSurface } from '../components/glass';
import { colors, spacing, radius, fonts, typographyScale } from '../constants/theme';
import { api } from '../services/api';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'CommunityManage'>;
type Rt = RouteProp<RootStackParamList, 'CommunityManage'>;

export default function CommunityManageScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const insets = useSafeAreaInsets();
  const name = route.params?.name;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [community, setCommunity] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);

  useLayoutEffect(() => { navigation.setOptions({ headerShown: false }); }, [navigation]);

  const load = useCallback(async () => {
    if (!name) { setLoading(false); return; }
    const [cRes, sRes] = await Promise.all([
      api<any>(`/communities/${encodeURIComponent(name)}`),
      api<any>(`/communities/${encodeURIComponent(name)}/stats`),
    ]);
    if (cRes.success && cRes.data) setCommunity(cRes.data);
    if (sRes.success && sRes.data?.stats) setStats(sRes.data.stats);
    setLoading(false);
  }, [name]);
  useFocusEffect(useCallback(() => { load(); }, [load]));

  const onRefresh = useCallback(async () => { setRefreshing(true); await load(); setRefreshing(false); }, [load]);

  const title = community?.displayName || community?.name || name || 'Community';

  const Header = (
    <GlassSurface variant="bar" radius={0} sheen={false} surfaceColor="transparent"
      style={[styles.header, { paddingTop: insets.top }]} contentStyle={styles.headerContent}>
      <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} accessibilityRole="button" accessibilityLabel="Back">
        <Ionicons name="chevron-back" size={26} color={colors.ink} />
      </TouchableOpacity>
      <Text style={styles.headerTitle} numberOfLines={1}>Manage</Text>
      <View style={{ width: 26 }} />
    </GlassSurface>
  );

  const StatCard = ({ icon, label, value }: { icon: any; label: string; value: number | string }) => (
    <GlassCard style={styles.statCard} padding={spacing.md}>
      <Ionicons name={icon} size={20} color={colors.primary} />
      <Text style={styles.statValue}>{typeof value === 'number' ? value.toLocaleString() : value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </GlassCard>
  );

  const ActionRow = ({ icon, label, onPress }: { icon: any; label: string; onPress: () => void }) => (
    <TouchableOpacity style={styles.actionRow} onPress={onPress} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel={label}>
      <View style={styles.actionIcon}><Ionicons name={icon} size={18} color={colors.primary} /></View>
      <Text style={styles.actionLabel}>{label}</Text>
      <Ionicons name="chevron-forward" size={18} color={colors.faint} />
    </TouchableOpacity>
  );

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      {Header}
      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + spacing.xxxl }]}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
      >
        <Text style={styles.communityName} numberOfLines={1}>{title}</Text>
        <Text style={styles.communityHandle} numberOfLines={1}>c/{community?.name || name}</Text>

        {loading ? (
          <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : (
          <>
            <View style={styles.statsRow}>
              <StatCard icon="people" label="Members" value={stats?.memberCount ?? community?.memberCount ?? 0} />
              <StatCard icon="document-text" label="Posts" value={stats?.postCount ?? 0} />
              <StatCard icon="flame" label="New / 7d" value={stats?.activeMembers ?? 0} />
            </View>

            <Text style={styles.sectionTitle}>Manage</Text>
            <GlassCard padding={0}>
              <ActionRow icon="color-palette-outline" label="Edit appearance & info" onPress={() => name && navigation.navigate('Community', { name })} />
              <View style={styles.rowDivider} />
              <ActionRow icon="people-outline" label="Members" onPress={() => name && navigation.navigate('Community', { name })} />
              <View style={styles.rowDivider} />
              <ActionRow icon="chatbubbles-outline" label="Channels" onPress={() => name && navigation.navigate('Community', { name })} />
            </GlassCard>

            {Array.isArray(stats?.topPostsThisWeek) && stats.topPostsThisWeek.length > 0 && (
              <>
                <Text style={styles.sectionTitle}>Top posts this week</Text>
                <GlassCard padding={0}>
                  {stats.topPostsThisWeek.map((p: any, i: number) => (
                    <TouchableOpacity key={p.id || i} style={styles.postRow} activeOpacity={0.7}
                      onPress={() => p.id && navigation.navigate('Post', { id: p.id })}>
                      <Text style={styles.postTitle} numberOfLines={1}>{p.title || 'Untitled post'}</Text>
                      <Text style={styles.postMeta}>{(p.score ?? 0)} · {(p.commentCount ?? 0)} 💬</Text>
                    </TouchableOpacity>
                  ))}
                </GlassCard>
              </>
            )}
          </>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  center: { paddingVertical: spacing.xxxl, alignItems: 'center' },
  header: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.cardBorder },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  headerTitle: { ...typographyScale.h3, color: colors.ink, ...fonts.semibold },
  content: { paddingHorizontal: spacing.xl, paddingTop: 100 },
  communityName: { ...typographyScale.h1, color: colors.ink, ...fonts.bold, textAlign: 'center' },
  communityHandle: { ...typographyScale.body, color: colors.muted, textAlign: 'center', marginTop: 2, marginBottom: spacing.lg },
  statsRow: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.lg },
  statCard: { flex: 1, alignItems: 'center' },
  statValue: { ...typographyScale.h2, color: colors.ink, ...fonts.bold, marginTop: spacing.xs, ...({ fontVariant: ['tabular-nums'] } as any) },
  statLabel: { ...typographyScale.caption, color: colors.muted, marginTop: 2, textAlign: 'center' },
  sectionTitle: { ...typographyScale.caption, color: colors.muted, ...fonts.semibold, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: spacing.sm, marginTop: spacing.md },
  actionRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  actionIcon: { width: 34, height: 34, borderRadius: 17, backgroundColor: colors.primary + '12', alignItems: 'center', justifyContent: 'center' },
  actionLabel: { ...typographyScale.body, color: colors.ink, ...fonts.medium, flex: 1 },
  rowDivider: { height: StyleSheet.hairlineWidth, backgroundColor: colors.cardBorder, marginLeft: spacing.md + 34 + spacing.md },
  postRow: { paddingHorizontal: spacing.md, paddingVertical: spacing.md, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md },
  postTitle: { ...typographyScale.body, color: colors.ink, flex: 1 },
  postMeta: { ...typographyScale.caption, color: colors.muted, ...({ fontVariant: ['tabular-nums'] } as any) },
});
